/**
 * Landing v3 — the store-review cards that ride over the world map (Figma
 * 15:1412, card 39:5723): initials avatar, country flag, title, rating,
 * excerpt. Real App Store reviews, quoted verbatim in the language they were
 * written in — they are testimony, not copy, so they are NOT localized. Cards
 * take their height from their content and alternate a vertical offset, so
 * the track reads as a loose masonry rather than one ruled row.
 *
 * Infinite horizontal scroll on the cheap: ONE track holding the cards twice,
 * moved by a CSS keyframe (`lv3-marquee`, index.css) that slides exactly one
 * copy's width and loops — transform-only, one compositor layer, nothing per
 * card. Pauses on hover; static under reduce-motion.
 *
 * Cards stand 40px apart (2.5rem, user decision: 16 read as cluttered). The
 * spacing lives INSIDE each copy — a gap between cards plus the same padding
 * after the last — so both copies are the same width and sliding by -50% lands
 * the second exactly where the first began: no jump when the loop wraps.
 */
import { Avatar } from '@flashka/shared/ui';

import { motion, useInView, useReducedMotion } from 'framer-motion';
import { StarIcon } from 'lucide-react';
import { useRef } from 'react';

import { cn } from '@/lib/utils';

import { GLASS_REVIEW_CLASS } from './landing-chrome';
import { LoopRow } from './landing-loop-row';

type Review = {
  /** The reviewer's store nickname — drives the DS Avatar's initials and its
   *  per-name palette colour. */
  author: string;
  /** ISO 3166-1 alpha-2, rendered as its flag emoji (as the design does). */
  country: string;
  title: string;
  body: string;
  stars: number;
};

/** The strongest recent App Store reviews, by title and excerpt. */
const REVIEWS: Review[] = [
  {
    author: 'Joe Durie',
    country: 'GB',
    title: 'Great for GCSE’s',
    stars: 5,
    body: 'It has saved my grades, from 5’s and 6’s to 8’s and 9’s. Professor Ka helped me to remove any confusion and add extra analysis into my work.',
  },
  {
    author: 'anitacasale04',
    country: 'IT',
    title: 'App eccezionale',
    stars: 5,
    body: 'La uso da circa un anno per lo studio universitario. Un’evoluzione eccezionale, soprattutto ora che hanno implementato gli esami orali e il calendario.',
  },
  {
    author: 'lucía_163',
    country: 'ES',
    title: 'MARAVILLA',
    stars: 5,
    body: 'La app te hace flashcards con cualquier cosa que le mandes, y si no entiendes algo tienes una IA que te ayuda. Ahora saco notazas.',
  },
  {
    author: 'navvym',
    country: 'MY',
    title: 'Incredible',
    stars: 5,
    body: 'By far the best flashcard app I’ve come across. Creating a deck is effortless while it keeps track of the cards you’ve mastered.',
  },
  {
    author: 'matmimat',
    country: 'FR',
    title: 'Parfait pour réviser',
    stars: 5,
    body: 'Je suis en médecine et ça m’aide énormément — tu peux vérifier les sources quand tu veux, tu vois où l’info a été prise dans ton cours.',
  },
  {
    author: 'clowiieee',
    country: 'PH',
    title: 'flashka enjoyer',
    stars: 5,
    body: 'I got a 💯 with the big help of flashka.ai. As someone taking my board exam next year, upgrading will defo be worth it.',
  },
  {
    author: 'Hutao321',
    country: 'MX',
    title: '10/10',
    stars: 5,
    body: 'Hace resúmenes y exámenes, lo cual me parece increíble para ser algo que no pide dinero. Sí, la recomiendo bastante.',
  },
  {
    author: 'Morlii',
    country: 'AT',
    title: 'Super app',
    stars: 5,
    body: 'Diese App ist echt der Wahnsinn — ich lerne seit 20 Minuten und habe mehr verstanden als mit anderen Apps.',
  },
  {
    author: 'No pierdas',
    country: 'GT',
    title: 'Para estudiar a último momento',
    stars: 5,
    body: 'Una aplicación muy completa. Te explica y hace simulaciones de pruebas muy efectivas. La recomiendo muchísimo.',
  },
  {
    author: 'ludovica5',
    country: 'IT',
    title: 'Ottima app e completa',
    stars: 5,
    body: 'C’è veramente tutto quello che ti può servire per uno studio efficace, come le mappe e la programmazione degli esami.',
  },
  {
    author: 'Isabella',
    country: 'SE',
    title: 'Jag älskar den',
    stars: 5,
    body: 'Den är den bästa och är super bra för att studera för prov.',
  },
  {
    author: 'luzldss',
    country: 'PY',
    title: 'Me encanta la interfaz',
    stars: 5,
    body: 'Súper intuitivo y fácil de usar. La repetición espaciada funciona muy bien, en especial para memorizar vocabulario.',
  },
];

/** ISO country code → regional-indicator flag emoji. */
/** A card's width (Figma 39:5723) — the `w-[229px]` on ReviewCard. */
const CARD_W = 229;
/** Space between two cards, px. */
const CARD_GAP = 40;
/** Drift speed, px a second — slowed from 42 (user: "pretty fast"): a card
 *  now takes about eight seconds to travel its own width. */
const DRIFT_PX_PER_S = 30;
/** The loop's duration: one copy's width at the drift speed. */
const LOOP_SECONDS = Math.round((REVIEWS.length * (CARD_W + CARD_GAP)) / DRIFT_PX_PER_S);

/**
 * The fade at each end of the band: the PAGE's own background — colour and
 * dots — laid over the cards and faded out toward the middle, so a card melts
 * into the page as it leaves on the left (and as it arrives on the right)
 * instead of being cut off. An overlay, not a mask: a mask on the band would
 * make it a backdrop root and kill the cards' glass (see ReviewsMarquee). The
 * dots are `bg-fixed` like the page's, so they land exactly on the page's.
 */
const EDGE_FADE_CLASS =
  'pointer-events-none absolute inset-y-0 w-[min(176px,14vw)] bg-background bg-dotted-pattern bg-dotted-size bg-fixed dark:bg-dotted-pattern-dark';
const fadeTo = (side: 'left' | 'right') => {
  const g = `linear-gradient(to ${side === 'left' ? 'right' : 'left'}, #000 12%, transparent)`;
  return { maskImage: g, WebkitMaskImage: g };
};

const flag = (cc: string) =>
  String.fromCodePoint(
    ...[...cc.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65),
  );

/** The phone layout's review card: the same card on an opaque fill — on a
 *  phone it sits below the map, not over it, so there is nothing to frost,
 *  and Ka waiting under the page never shows through it. */
const SOLID_REVIEW_CLASS = 'border border-secondary-200 bg-secondary-50 shadow-card-row';

function ReviewCard({
  review,
  offset,
  solid = false,
  className,
}: {
  review: Review;
  offset: boolean;
  solid?: boolean;
  className?: string;
}) {
  return (
    // Figma 39:5723: 229px wide, radius 40, 24px padding, height from content;
    // avatar + flag, title, five stars, then the excerpt.
    <article
      className={cn(
        'flex w-[229px] shrink-0 flex-col gap-4 self-start rounded-[40px] p-6',
        offset && 'mt-12',
        solid ? SOLID_REVIEW_CLASS : GLASS_REVIEW_CLASS,
        className,
      )}>
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <Avatar name={review.author} size="m" />
          <span className="text-[18px] leading-[26px]" aria-hidden>
            {flag(review.country)}
          </span>
        </div>
        {/* The frame's H3 is a type style, not a heading: two dozen review
            titles (the loop renders its track twice) flooded the outline. */}
        <p className="text-[18px] font-semibold leading-[26px] text-secondary-800">
          {review.title}
        </p>
        <div className="flex gap-1" aria-label={`${review.stars} / 5`}>
          {Array.from({ length: review.stars }, (_, i) => (
            <StarIcon
              key={i}
              className="h-5 w-5 fill-amber-400 text-amber-400"
              aria-hidden
            />
          ))}
        </div>
      </div>
      <p className="text-[16px] font-semibold leading-[22px] text-fg-subtle">
        {review.body}
      </p>
    </article>
  );
}

export function ReviewsMarquee({
  play,
  className,
}: {
  play: boolean;
  className?: string;
}) {
  const reduceMotion = useReducedMotion();
  // The loop only runs while the band is near the screen: off screen it still
  // moved a 6,500px strip of 24 blurred cards every frame (the deck above
  // paid for it).
  const ref = useRef<HTMLDivElement>(null);
  const near = useInView(ref, { margin: '200px 0px' });
  return (
    <div ref={ref} className={cn('relative w-full', className)}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: play ? 1 : 0 }}
        transition={
          reduceMotion ? { duration: 0 } : { duration: 0.5, delay: play ? 0.6 : 0 }
        }
        // `overflow-clip`: a hidden overflow is still scrollable by focus.
        // NO mask / filter / blend on this band or the track: any of them makes
        // the band a "backdrop root", and the cards' backdrop-filter then samples
        // only the band (transparent) instead of the map behind it — the dots
        // showed crisp through 70% white, which is not glass. The edges fade
        // through the overlays below instead.
        className="group w-full overflow-clip">
        <div
          className="flex w-max items-start will-change-transform [animation-play-state:running] group-hover:[animation-play-state:paused]"
          // Longhands only: a pause set beside the `animation` shorthand is
          // dropped on the next render and restarts the loop. Inline, so
          // reduced motion is decided here too (a class would lose to it).
          style={{
            animationName: reduceMotion ? 'none' : 'lv3-marquee',
            animationDuration: `${LOOP_SECONDS}s`,
            animationTimingFunction: 'linear',
            animationIterationCount: 'infinite',
            animationPlayState: near ? undefined : 'paused',
          }}>
          {[0, 1].map((copy) => (
            <div
              key={copy}
              className="flex items-start"
              style={{ gap: CARD_GAP, paddingRight: CARD_GAP }}
              aria-hidden={copy === 1}>
              {REVIEWS.map((review, i) => (
                <ReviewCard key={i} review={review} offset={i % 2 === 1} />
              ))}
            </div>
          ))}
        </div>
      </motion.div>
      {/* Mounted only near the screen: a `bg-fixed` box anywhere in the page
          puts Safari's whole scroll on the main thread. */}
      {near && (
        <>
          <div
            aria-hidden
            className={cn(EDGE_FADE_CLASS, 'left-0')}
            style={fadeTo('left')}
          />
          <div
            aria-hidden
            className={cn(EDGE_FADE_CLASS, 'right-0')}
            style={fadeTo('right')}
          />
        </>
      )}
    </div>
  );
}

/**
 * The reviews on the phone layout (user 2026-09-29): the desktop's endless
 * drift, in a row a finger can also push either way (`LoopRow`). Below the
 * map rather than over it.
 */
export function ReviewsRow({ className }: { className?: string }) {
  return (
    <LoopRow speed={DRIFT_PX_PER_S} gap={16} className={className}>
      {REVIEWS.map((review, i) => (
        <ReviewCard key={i} review={review} offset={false} solid />
      ))}
    </LoopRow>
  );
}
